import type { Channel } from "@expert/shared-types";
import { bool, customCleanEnv, str, url } from "envalid";
import type { LevelOrString } from "pino";

interface Env {
    mode: string;
    disableConsoleLogs: boolean;
    isProdMode: boolean;
    commitHash: string;
    appName: string;
    channel: Channel;
    accessTokenGeneratorPath: string;
    oidcSsoUrl: string;
    oidcSsoClientId: string;
    oidcSsoAdditionalScope: string;
    expertWorkspaceWebSocketUrl?: string;
    gaiaExternal: boolean;
    gaiaWebsocketUrl: string;
    gaiaApiUrl: string;
    fullstoryOrgId: string;
    appcuesEnabled: boolean;
    appcuesUrl: string;
    apiBaseUrl: string;
    contentful: {
        accessToken: string;
        space: string;
        host: string;
    };
    expertFeedbackUrl: string;
    logCollectorUrl: `${string}/log`;
    metricsCollectorUrl: `${string}/metrics`;
    logLevel: {
        api: LevelOrString;
        console: LevelOrString;
    };
    ivrDetailsUrl: string;
    fullstoryEnabled: boolean;
    expertUniversityWebsocketUrl?: string;
    useSocketIO: boolean;
}

export const env = customCleanEnv(
    import.meta.env,
    {
        MODE: str(),
        VITE_DISABLE_CONSOLE_LOGS: bool({ default: false }),
        VITE_ENV_TYPE: str(),
        PROD: bool(),
        COMMIT_HASH: str(),
        VITE_APP_NAME: str(),
        VITE_GAIA_EXTERNAL: bool(),
        VITE_GAIA_WEBSOCKET_URL: url(),
        VITE_GAIA_API_URL: url(),
        VITE_FULLSTORY_ORG_ID: str(),
        VITE_CHANNEL: str({ choices: ["voice", "chat"] }),
        VITE_OIDC_TOKEN_GENERATOR_PATH: str({ default: "api/service-provider/generate-access-token/twilio" }),
        VITE_OIDC_SSO_URL: url(),
        VITE_OIDC_SSO_CLIENT_ID: str(),
        VITE_OIDC_SSO_ADDITIONAL_SCOPE: str(),
        VITE_APPCUES_ENABLED: bool(),
        VITE_APPCUES_URL: str(),
        VITE_API_BASE_URL: url(),
        VITE_CONTENTFUL_ACCESS_TOKEN: str(),
        VITE_CONTENTFUL_SPACE: str(),
        VITE_CONTENTFUL_HOST: str(),
        VITE_LOG_LEVEL_CONSOLE: str({ default: "trace" }),
        VITE_LOG_LEVEL_API: str({ default: "warn" }),
        VITE_IVR_DETAILS_URL: url(),
        VITE_USE_SOCKET_IO: bool({ default: false }),
        VITE_WEBSOCKET_URL: url({ default: undefined }),
        VITE_FULLSTORY_ENABLED: bool({ default: false }),
        VITE_EXPERT_UNIVERSITY_WEBSOCKET_URL: url({ default: undefined }),
    },
    (inputEnv): Env => ({
        mode: inputEnv.MODE,
        disableConsoleLogs: inputEnv.VITE_DISABLE_CONSOLE_LOGS,
        isProdMode: inputEnv.VITE_ENV_TYPE === "production" && inputEnv.PROD,
        commitHash: inputEnv.COMMIT_HASH,
        appName: inputEnv.VITE_APP_NAME,
        channel: inputEnv.VITE_CHANNEL,
        accessTokenGeneratorPath: inputEnv.VITE_OIDC_TOKEN_GENERATOR_PATH,
        oidcSsoUrl: inputEnv.VITE_OIDC_SSO_URL,
        oidcSsoClientId: inputEnv.VITE_OIDC_SSO_CLIENT_ID,
        oidcSsoAdditionalScope: inputEnv.VITE_OIDC_SSO_ADDITIONAL_SCOPE,
        gaiaExternal: inputEnv.VITE_GAIA_EXTERNAL ?? false,
        gaiaWebsocketUrl: inputEnv.VITE_GAIA_WEBSOCKET_URL,
        gaiaApiUrl: inputEnv.VITE_GAIA_API_URL,
        fullstoryOrgId: inputEnv.VITE_FULLSTORY_ORG_ID,
        appcuesEnabled: inputEnv.VITE_APPCUES_ENABLED,
        appcuesUrl: inputEnv.VITE_APPCUES_URL,
        apiBaseUrl: inputEnv.VITE_API_BASE_URL,
        contentful: {
            accessToken: inputEnv.VITE_CONTENTFUL_ACCESS_TOKEN,
            space: inputEnv.VITE_CONTENTFUL_SPACE,
            host: inputEnv.VITE_CONTENTFUL_HOST,
        },
        expertFeedbackUrl: `${inputEnv.VITE_API_BASE_URL}/api/feedback/v2/expert`,
        logCollectorUrl: `${inputEnv.VITE_API_BASE_URL}/log`,
        metricsCollectorUrl: `${inputEnv.VITE_API_BASE_URL}/metrics`,
        logLevel: {
            api: inputEnv.VITE_LOG_LEVEL_API,
            console: inputEnv.VITE_LOG_LEVEL_CONSOLE,
        },
        ivrDetailsUrl: inputEnv.VITE_IVR_DETAILS_URL,
        useSocketIO: inputEnv.VITE_USE_SOCKET_IO,
        expertWorkspaceWebSocketUrl: inputEnv.VITE_WEBSOCKET_URL,
        fullstoryEnabled: inputEnv.VITE_FULLSTORY_ENABLED,
        expertUniversityWebsocketUrl: inputEnv.VITE_EXPERT_UNIVERSITY_WEBSOCKET_URL,
    }),
);
